import { ListResponseModel, toQueryStringWithArray } from ".";
import { Money } from "../model/common/money";
import { SafetyStaticCardData } from "../model/safety/safety-static-card-data";
import { StaffReportResponse } from "../model/safety/staff-report";
import { StaffVerifyResponse } from "../model/safety/staff-verify";
import { SectionDefinition } from "../model/service-request/section-definition";
import { ServiceRequestType, ServiceRequestWithHistoryDataSteps } from "../model/service-request/service-request";
import { ServiceRequestInstance, ServiceRequestInstanceParams } from "../model/service-request/service-request-instance";
import { ServiceRequestInstanceDetailsResponse } from "../model/service-request/service-request-instance-details";
import { StepDefinition } from "../model/workflows/step-definition";
import { ClaimStepRequest, StepInstanceQueryParams, StepInstanceResponse, UnassignStepRequest } from "../model/workflows/step-instance";
import { StepStaticCardsResponse, SupervisorStepsQueryParams } from "../model/workflows/supervisor-steps";
import { WorkflowDefinition } from "../model/workflows/workflow-definition";
import { apiSlice } from "../stores/base/api-slice";
import { PaginatedList } from "../model/common/paginated-list";

// service for admin leads and provider reques  ts
const baseUrl = `${process.env.REACT_APP_SERVER_URL}`;

export interface PaginationAndSearchParams {
	page: number;
	pageSize: number;
	search?: string;
}

export interface ServiceRequestTypePrams {
	page: number;
	pageSize: number;
	isPublished: boolean;
	search?: string;
}

export interface ServiceRequestIdParams {
	serviceRequestId: string;
}

export interface EditServiceRequestRequest {
	serviceRequestId: string;
	name: string;
	description?: string;
	sectionDefinitions: SectionDefinition[];
	price?: Money;
	payOnSubmit?: boolean;
	isExternal?: boolean;
}

export interface CreateWorkflowRequest {
	name: string;
	description: string;
	serviceRequestId: string;
}

export interface CreateWorkflowResponse {
	workflowId: string;
}

export interface EditWorkflowBasicInfo {
	workflowId: string;
	name: string;
	description: string;
}

export interface EditWorkflowSteps {
	workflowId: string;
	stepDefinitions: StepDefinition[];
}

export interface SubmitStepAttributeRequest {
	attributeDefinitionId: string;
	selectedOptionIds?: string[];
	stringValue?: string;
	doubleValue?: number;
	boolValue?: boolean;
	dateValue?: string;
	file?: File;
}

export interface SubmitStepSectionRequest {
	sectionDefinitionId: string;
	attributes: SubmitStepAttributeRequest[];
}

export interface SubmitStepAdditionalServiceRequest {
	serviceRequestId: string;
	requestTypeId: string;
	sections: SubmitStepSectionRequest[];
}

export interface SubmitStepRequest {
	stepInstanceId: string;
	takenActionId: string;
	NextDepartmentId?: string;
	additionalServiceRequest?: SubmitStepAdditionalServiceRequest;
}

export interface SupervisorAssignStepRequest {
	stepInstanceId: string;
	assignToUserId: string;
}

export interface SupervisorUnassignStepRequest {
	stepInstanceId: string;
}

export interface StaffVerifyParams {
	page: number;
	pageSize: number;
	searchPhrase?: string;
	from?: string;
	to?: string;
	reasonIds?: string[];
}

export interface StaticCardDataParams {
	searchPhrase?: string;
	from?: string;
	to?: string;
	reasonIds?: string[];
}

export interface RequestTypeFilterOption {
	id: string;
	name: string;
}

export interface GetPageOfAllServiceRequestInstancesRequest {
	page: number;
	pageSize: number;
	search?: string;
	departmentIds?: string[];
	isSubmittedBySupportAgent?: boolean;
	requestTypeIds?: string[];
	slaStatuses?: number[];
	from?: string;
	to?: string;
}

export interface SubmitServiceRequestAttributePayload {
	attributeDefinitionId: string;
	selectedOptionIds?: string[];
	stringValue?: string;
	doubleValue?: number;
	boolValue?: boolean;
	dateValue?: string;
	file?: File;
}

export interface SubmitServiceRequestSectionPayload {
	sectionDefinitionId: string;
	attributes: SubmitServiceRequestAttributePayload[];
}

export interface SubmitServiceRequestPayload {
	serviceRequestId: string;
	requestTypeId: string;
	sections: SubmitServiceRequestSectionPayload[];
}

export interface SubmitServiceRequestWithWalletPayload extends SubmitServiceRequestPayload {
	pin: string;
}

export interface SubmitServiceRequestWithCardPayload extends SubmitServiceRequestPayload {
	pin: string;
	returnUrl: string;
	paymentTypes: string[];
}

export interface SubmitServiceRequestWithWalletResponse {
	reference: string;
}

export interface SubmitServiceRequestWithCardResponse {
	reference: string;
	url: string;
}

export interface PayServiceRequestWithWalletPayload {
	serviceRequestId: string;
	pin: string;
}

export interface PayServiceRequestWithCardPayload {
	serviceRequestId: string;
	pin: string;
	returnUrl: string;
	paymentTypes: string[];
}

export interface PayServiceRequestWithCardResponse {
	reference: string;
	url: string;
}

export interface SupportAgentSubmitSRPayload {
	serviceRequestId: string;
	requestTypeId: string;
	sections: SubmitServiceRequestSectionPayload[];
	customerDetails: CustomerDetails;
	payWithLink: boolean;
}
export interface CustomerDetails {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	address: string;
	meterNumber: string;
	// "prepaid" | "postpaid" — tells the backend whether meterNumber is a
	// meter number or an account number when stamping the hidden fields.
	meterType: string;
}
export interface SupportAgentSubmitSRWithCardPayload extends SupportAgentSubmitSRPayload {
	returnUrl: string;
}

export interface SupportAgentSubmitSRWithCardResponse {
	reference: string;
	url: string;
}

export interface SupportAgentSubmitSRResponse {
	id: string;
}

const buildServiceRequestFormData = (data: SubmitServiceRequestPayload, channel: string = "CustomerWebApp"): FormData => {
	const formData = new FormData();

	formData.append("serviceRequestId", data.serviceRequestId);
	formData.append("requestTypeId", data.requestTypeId);
	// Submission channel — backend stamps the hidden "Channel of Complaint"
	// field from this value (Customer Web App / Admin Web App / Mobile App).
	formData.append("channel", channel);

	data.sections.forEach((section, sectionIndex) => {
		formData.append(`sections[${sectionIndex}].sectionDefinitionId`, section.sectionDefinitionId);

		section.attributes.forEach((attribute, attrIndex) => {
			const prefix = `sections[${sectionIndex}].attributes[${attrIndex}]`;

			formData.append(`${prefix}.attributeDefinitionId`, attribute.attributeDefinitionId);

			if (attribute.selectedOptionIds && attribute.selectedOptionIds.length > 0) {
				attribute.selectedOptionIds.forEach((id, idIndex) => {
					formData.append(`${prefix}.selectedOptionIds[${idIndex}]`, id);
				});
			}

			if (attribute.stringValue !== undefined && attribute.stringValue !== null) {
				formData.append(`${prefix}.stringValue`, attribute.stringValue);
			}

			if (attribute.doubleValue !== undefined && attribute.doubleValue !== null) {
				formData.append(`${prefix}.doubleValue`, attribute.doubleValue.toString());
			}

			if (attribute.boolValue !== undefined && attribute.boolValue !== null) {
				formData.append(`${prefix}.boolValue`, attribute.boolValue.toString());
			}

			if (attribute.dateValue) {
				formData.append(`${prefix}.dateValue`, attribute.dateValue);
			}

			if (attribute.file instanceof File) {
				formData.append(`${prefix}.file`, attribute.file);
			}
		});
	});

	return formData;
};

const buildSupportAgentSRFormData = (data: SupportAgentSubmitSRPayload): FormData => {
	const formData = buildServiceRequestFormData(data, "AdminWebApp");
	formData.append("customerDetails.firstName", data.customerDetails.firstName);
	formData.append("customerDetails.lastName", data.customerDetails.lastName);
	formData.append("customerDetails.email", data.customerDetails.email);
	formData.append("customerDetails.phoneNumber", data.customerDetails.phoneNumber);
	formData.append("customerDetails.address", data.customerDetails.address);
	formData.append("customerDetails.meterNumber", data.customerDetails.meterNumber);
	formData.append("customerDetails.meterType", data.customerDetails.meterType);
	formData.append("payWithLink", data.payWithLink.toString());
	return formData;
};

const createQueryString = (params: PaginationAndSearchParams) => {
	let queryString = "";

	if (params.page !== undefined) {
		queryString += `page=${params.page}&`;
	}

	if (params.pageSize !== undefined) {
		queryString += `pageSize=${params.pageSize}&`;
	}
	if (params.search !== undefined) {
		queryString += `search=${params.search.trim()}&`;
	}
	return queryString.endsWith("&") ? queryString.slice(0, -1) : queryString;
};

const createQueryStringStaff = (params: StaffVerifyParams) => {
	let queryString = "";

	if (params.page !== undefined) {
		queryString += `Page=${params.page}&`;
	}

	if (params.pageSize !== undefined) {
		queryString += `PageSize=${params.pageSize}&`;
	}
	if (params.searchPhrase !== undefined) {
		queryString += `SearchPhrase=${params.searchPhrase}&`;
	}
	if (params.from !== undefined) {
		queryString += `From=${params.from}&`;
	}
	if (params.to !== undefined) {
		queryString += `To=${params.to}&`;
	}
	if (params.reasonIds !== undefined) {
		params.reasonIds.forEach(x => {
			queryString += `ReasonIds=${x}&`;
		});
	}
	return queryString.endsWith("&") ? queryString.slice(0, -1) : queryString;
};

const createQueryStringStaticCard = (params: StaticCardDataParams) => {
	let queryString = "";

	if (params.searchPhrase !== undefined) {
		queryString += `SearchPhrase=${params.searchPhrase}&`;
	}
	if (params.from !== undefined) {
		queryString += `From=${params.from}&`;
	}
	if (params.to !== undefined) {
		queryString += `To=${params.to}&`;
	}
	if (params.reasonIds !== undefined) {
		params.reasonIds.forEach(x => {
			queryString += `ReasonIds=${x}&`;
		});
	}
	return queryString.endsWith("&") ? queryString.slice(0, -1) : queryString;
};

const serviceBaseUrl = "/customersupportservice/service-requests";
const customerSupportEndpoint = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getServiceRequests: builder.query<PaginatedList<ServiceRequestType>, PaginationAndSearchParams>({
			query: params => ({
				url: `${serviceBaseUrl}/definition?${createQueryString(params)}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequests"],
		}),
		getQuickServiceRequests: builder.query<Array<ServiceRequestType>, void>({
			query: () => ({
				url: `${serviceBaseUrl}/get-quick-service-requests`,
				method: "GET",
			}),
			providesTags: ["QuickServiceRequest"],
		}),
		getMostRecentServiceRequests: builder.query<ServiceRequestWithHistoryDataSteps, void>({
			query: () => ({
				url: `${serviceBaseUrl}/get-most-recent-service-requests`,
				method: "GET",
			}),
			providesTags: ["RecentServiceRequest"],
		}),
		addNewServiceRequest: builder.mutation<any, ServiceRequestType>({
			query: body => ({
				url: `${serviceBaseUrl}`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequests"],
		}),
		publishServiceRequest: builder.mutation<any, ServiceRequestIdParams>({
			query: body => ({
				url: `${serviceBaseUrl}/publish`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequests"],
		}),
		unpublishServiceRequest: builder.mutation<any, ServiceRequestIdParams>({
			query: body => ({
				url: `${serviceBaseUrl}/unpublish`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequests"],
		}),
		editServiceRequest: builder.mutation<any, EditServiceRequestRequest>({
			query: body => ({
				url: `${serviceBaseUrl}/edit`,
				method: "PUT",
				body,
			}),
			invalidatesTags: ["ServiceRequests"],
		}),
		getServiceRequest: builder.query<ServiceRequestType, string>({
			query: id => ({
				url: `${serviceBaseUrl}/get-service-request-definition/${id}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequests"],
		}),
		getServiceRequestInstances: builder.query<ListResponseModel<Array<ServiceRequestInstance>>, ServiceRequestInstanceParams>({
			query: params => ({
				url: `${serviceBaseUrl}/get-service-request-instances` + toQueryStringWithArray(params),
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstances"],
		}),
		getServiceRequestInstance: builder.query<ServiceRequestInstance, number>({
			query: id => ({
				url: `${serviceBaseUrl}/get-service-request-instance-utility-admin/${id}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstance"],
		}),
		////
		getServiceRequestTypes: builder.query<PaginatedList<ServiceRequestType>, ServiceRequestTypePrams>({
			query: (params) => ({
				url: `${serviceBaseUrl}/get-page-service-requests`,
				method: "GET",
				params,
			}),
			// providesTags: ["ServiceRequestInstance"],
		}),
		getServiceRequestDefinitionForCustomer: builder.query<ServiceRequestType, string>({
			query: id => ({
				url: `${serviceBaseUrl}/get-service-request-definition-customer/${id}`,
				method: "GET",
			}),
		}),
		getServiceRequestInstanceCustomer: builder.query<ServiceRequestInstance, number>({
			query: id => ({
				url: `/service-request/get-service-request-instance-customer/${id}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstance"],
		}),
		assignServiceRequest: builder.mutation<any, ServiceRequestIdParams>({
			query: body => ({
				url: `${serviceBaseUrl}/assign`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		moveServiceRequestInProgress: builder.mutation<any, ServiceRequestIdParams>({
			query: body => ({
				url: `${serviceBaseUrl}/move-in-progress`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		completeServiceRequest: builder.mutation<any, ServiceRequestIdParams>({
			query: body => ({
				url: `${serviceBaseUrl}/complete`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}), //Safety
		getStaffVerify: builder.query<ListResponseModel<Array<StaffVerifyResponse>>, StaffVerifyParams>({
			query: params => ({
				url: `/customersupportservice/aedc/safety/get-staff-by-filter?${createQueryStringStaff(params)}`,
				method: "GET",
			}),
			providesTags: ["SafetyStaffInsighst"],
		}),
		getStaffReports: builder.query<ListResponseModel<Array<StaffReportResponse>>, StaffVerifyParams>({
			query: params => ({
				url: `/customersupportservice/aedc/safety/get-safety-report-by-filter?${createQueryStringStaff(params)}`,
				method: "GET",
			}),
			providesTags: ["SafetyStaffInsighst"],
		}),
		getStaticCardData: builder.query<SafetyStaticCardData, StaticCardDataParams | void>({
			query: params => ({
				url: `/customersupportservice/aedc/safety/get-static-card-data${params ? `?${createQueryStringStaticCard(params)}` : ""}`,
				method: "GET",
			}),
			providesTags: ["SafetyStaffInsighst"],
		}),
		getOngoingServiceRequests: builder.query<ListResponseModel<Array<any>>, PaginationAndSearchParams>({
			query: params => ({
				url: `${serviceBaseUrl}/get-ongoing-service-requests`,
				params,
			}),
		}),
		getCompletedServiceRequests: builder.query<ListResponseModel<Array<any>>, PaginationAndSearchParams>({
			query: params => ({
				url: `${serviceBaseUrl}/get-completed-service-requests`,
				params,
			}),
		}),
		getAllWorkflows: builder.query<PaginatedList<WorkflowDefinition>, PaginationAndSearchParams>({
			query: params => ({
				url: `/customersupportservice/workflow/get-all-workflows?${createQueryString(params)}`,
				method: "GET",
			}),
			providesTags: ["Workflows"],
		}),
		getWorkflowById: builder.query<WorkflowDefinition, string>({
			query: id => ({
				url: `/customersupportservice/workflow/get-workflow-by-id?WorkflowId=${id}`,
				method: "GET",
			}),
			providesTags: ["Workflows"],
		}),
		createWorkflow: builder.mutation<CreateWorkflowResponse, CreateWorkflowRequest>({
			query: data => ({
				url: "/customersupportservice/workflow/create-workflow",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Workflows"],
		}),
		editWorkflowBasicInfo: builder.mutation<void, EditWorkflowBasicInfo>({
			query: data => ({
				url: "/customersupportservice/workflow/edit-workflow-basic-info",
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Workflows"],
		}),
		editWorkflowSteps: builder.mutation<void, EditWorkflowSteps>({
			query: data => ({
				url: "/customersupportservice/workflow/edit-workflow-steps",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Workflows"],
		}),
		getStepInstanceById: builder.query<StepInstanceResponse, string>({
			query: data => ({
				url: `/customersupportservice/workflow/get-step-instance-by-id?StepInstanceId=${data}`,
				method: "GET",
			}),
			providesTags: ["StepInstance"],
		}),
		submitStep: builder.mutation<any, SubmitStepRequest>({
			query: data => {
				// Use FormData when files are present
				const formData = new FormData();

				// Add basic fields
				formData.append("stepInstanceId", data.stepInstanceId);
				formData.append("takenActionId", data.takenActionId);
				if (data.NextDepartmentId) {
					formData.append("nextDepartmentId", data.NextDepartmentId);
				}

				// Add additionalServiceRequest if it exists
				if (data.additionalServiceRequest) {
					formData.append("additionalServiceRequest.serviceRequestId", data.additionalServiceRequest.serviceRequestId);
					formData.append("additionalServiceRequest.requestTypeId", data.additionalServiceRequest.requestTypeId);

					// Add sections
					data.additionalServiceRequest.sections.forEach((section, sectionIndex) => {
						formData.append(`additionalServiceRequest.sections[${sectionIndex}].sectionDefinitionId`, section.sectionDefinitionId);

						// Add attributes
						section.attributes.forEach((attribute, attrIndex) => {
							formData.append(
								`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].attributeDefinitionId`,
								attribute.attributeDefinitionId
							);

							if (attribute.selectedOptionIds && attribute.selectedOptionIds.length > 0) {
								attribute.selectedOptionIds.forEach((id, idIndex) => {
									formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].selectedOptionIds[${idIndex}]`, id);
								});
							}

							if (attribute.stringValue !== undefined && attribute.stringValue !== null) {
								formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].stringValue`, attribute.stringValue);
							}

							if (attribute.doubleValue !== undefined && attribute.doubleValue !== null) {
								formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].doubleValue`, attribute.doubleValue.toString());
							}

							if (attribute.boolValue !== undefined && attribute.boolValue !== null) {
								formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].boolValue`, attribute.boolValue.toString());
							}

							if (attribute.dateValue) {
								formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].dateValue`, attribute.dateValue);
							}

							if (attribute.file instanceof File) {
								formData.append(`additionalServiceRequest.sections[${sectionIndex}].attributes[${attrIndex}].file`, attribute.file);
							}
						});
					});
				}

				return {
					url: `/customersupportservice/workflow/submit-step`,
					method: "POST",
					body: formData,
				};
			},
			invalidatesTags: ["StepInstance"],
		}),
		getAllClaimableSteps: builder.query<ListResponseModel<StepInstanceResponse[]>, StepInstanceQueryParams>({
			query: params => ({
				url: `customersupportservice/workflow/get-all-claimable-steps${toQueryStringWithArray(params)}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstances"],
		}),
		getAllAssignedSteps: builder.query<ListResponseModel<StepInstanceResponse[]>, StepInstanceQueryParams>({
			query: params => ({
				url: `customersupportservice/workflow/get-all-assigned-steps${toQueryStringWithArray(params)}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstances"],
		}),
		claimStep: builder.mutation<void, ClaimStepRequest>({
			query: body => ({
				url: "customersupportservice/workflow/claim-step",
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		unassignStep: builder.mutation<void, UnassignStepRequest>({
			query: body => ({
				url: "customersupportservice/workflow/unassign-step",
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		getStepStaticCards: builder.query<StepStaticCardsResponse, { departmentId: string; from?: string; to?: string }>({
			query: params => ({
				url: `customersupportservice/workflow/supervisor/static-cards${toQueryStringWithArray(params)}`,
				method: "GET",
			}),
			providesTags: ["SupervisorSteps"],
		}),
		getSupervisorSteps: builder.query<ListResponseModel<StepInstanceResponse[]>, SupervisorStepsQueryParams>({
			query: params => ({
				url: `customersupportservice/workflow/supervisor/steps${toQueryStringWithArray(params)}`,
				method: "GET",
			}),
			providesTags: ["SupervisorSteps"],
		}),
		supervisorAssignStep: builder.mutation<void, SupervisorAssignStepRequest>({
			query: body => ({
				url: "customersupportservice/workflow/supervisor/assign-step",
				method: "POST",
				body,
			}),
			invalidatesTags: ["SupervisorSteps", "StepInstance"],
		}),
		supervisorUnassignStep: builder.mutation<void, SupervisorUnassignStepRequest>({
			query: body => ({
				url: "customersupportservice/workflow/supervisor/unassign-step",
				method: "POST",
				body,
			}),
			invalidatesTags: ["SupervisorSteps", "StepInstance"],
		}),
		getRequestTypesForFilter: builder.query<RequestTypeFilterOption[], { departmentId?: string }>({
			query: params => ({
				url: `customersupportservice/workflow/get-request-types-for-filter${toQueryStringWithArray(params)}`,
				method: "GET",
			}),
		}),
		submitServiceRequest: builder.mutation<any, SubmitServiceRequestPayload>({
			query: data => {
				const formData = buildServiceRequestFormData(data);
				console.log("[Submit SR] FormData entries:");
				formData.forEach((value, key) => console.log(`  ${key} =`, value));
				return {
					url: `${serviceBaseUrl}/submit`,
					method: "POST",
					body: formData,
				};
			},
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		submitServiceRequestWithWallet: builder.mutation<SubmitServiceRequestWithWalletResponse, SubmitServiceRequestWithWalletPayload>({
			query: data => {
				const formData = buildServiceRequestFormData(data);
				formData.append("pin", data.pin);
				return {
					url: `${serviceBaseUrl}/submit-with-wallet`,
					method: "POST",
					body: formData,
				};
			},
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		payServiceRequestWithWallet: builder.mutation<{ reference: string }, PayServiceRequestWithWalletPayload>({
			query: body => ({
				url: `customersupportservice/service-requests/pay-with-wallet`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		payServiceRequestWithCard: builder.mutation<PayServiceRequestWithCardResponse, PayServiceRequestWithCardPayload>({
			query: body => ({
				url: `customersupportservice/service-requests/pay-with-card`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		submitServiceRequestWithCard: builder.mutation<SubmitServiceRequestWithCardResponse, SubmitServiceRequestWithCardPayload>({
			query: data => {
				const formData = buildServiceRequestFormData(data);
				formData.append("pin", data.pin);
				formData.append("returnUrl", data.returnUrl);
				data.paymentTypes.forEach((type, index) => {
					formData.append(`paymentTypes[${index}]`, type);
				});
				return {
					url: `${serviceBaseUrl}/submit-with-card`,
					method: "POST",
					body: formData,
				};
			},
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		supportAgentSubmitSR: builder.mutation<SupportAgentSubmitSRResponse, SupportAgentSubmitSRPayload>({
			query: data => ({
				url: `${serviceBaseUrl}/submit-support-agent`,
				method: "POST",
				body: buildSupportAgentSRFormData(data),
			}),
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		supportAgentSubmitSRWithCard: builder.mutation<SupportAgentSubmitSRWithCardResponse, SupportAgentSubmitSRWithCardPayload>({
			query: data => {
				const formData = buildSupportAgentSRFormData(data);
				formData.append("returnUrl", data.returnUrl);
				return {
					url: `${serviceBaseUrl}/submit-with-card-support-agent`,
					method: "POST",
					body: formData,
				};
			},
			invalidatesTags: ["ServiceRequestInstances"],
		}),
		
		getPageOfAllServiceRequestInstances: builder.query<PaginatedList<ServiceRequestInstanceDetailsResponse>, GetPageOfAllServiceRequestInstancesRequest>({
			query: params => ({
				url: `${serviceBaseUrl}/support-desk/requests`,
				method: "GET",
				params,
			}),
			providesTags: ["ServiceRequestInstances"],
		}),
		getServiceRequestInstanceDetailsById: builder.query<ServiceRequestInstanceDetailsResponse, string>({
			query: id => ({
				url: `${serviceBaseUrl}/get-service-request-instance-details/${id}`,
				method: "GET",
			}),
			providesTags: ["ServiceRequestInstance"],
		}),
		getAllRequestTypes: builder.query<RequestTypeFilterOption[], void>({
			query: () => ({
				url: `${serviceBaseUrl}/get-all-request-types`,
				method: "GET",
			}),
		}),
	}),
});

export const {
	useLazyGetOngoingServiceRequestsQuery,
	useLazyGetCompletedServiceRequestsQuery,
	useGetMostRecentServiceRequestsQuery,
	useGetQuickServiceRequestsQuery,
	useLazyGetServiceRequestsQuery,
	useAddNewServiceRequestMutation,
	usePublishServiceRequestMutation,
	useUnpublishServiceRequestMutation,
	useEditServiceRequestMutation,
	useLazyGetServiceRequestQuery,
	useLazyGetServiceRequestInstancesQuery,
	useLazyGetServiceRequestInstanceQuery,
	useGetServiceRequestInstanceCustomerQuery,
	useAssignServiceRequestMutation,
	useMoveServiceRequestInProgressMutation,
	useCompleteServiceRequestMutation,
	useLazyGetStaffVerifyQuery,
	useLazyGetStaffReportsQuery,
	useGetStaticCardDataQuery,
	useLazyGetAllWorkflowsQuery,
	useLazyGetWorkflowByIdQuery,
	useCreateWorkflowMutation,
	useEditWorkflowBasicInfoMutation,
	useEditWorkflowStepsMutation,
	useLazyGetStepInstanceByIdQuery,
	useSubmitStepMutation,
	useLazyGetAllClaimableStepsQuery,
	useLazyGetAllAssignedStepsQuery,
	useClaimStepMutation,
	useUnassignStepMutation,
	useLazyGetStepStaticCardsQuery,
	useLazyGetSupervisorStepsQuery,
	useSupervisorAssignStepMutation,
	useSupervisorUnassignStepMutation,
	useLazyGetRequestTypesForFilterQuery,
	useLazyGetServiceRequestTypesQuery,
	useLazyGetServiceRequestDefinitionForCustomerQuery,
	useSubmitServiceRequestMutation,
	useSubmitServiceRequestWithWalletMutation,
	useSubmitServiceRequestWithCardMutation,
	usePayServiceRequestWithWalletMutation,
	usePayServiceRequestWithCardMutation,
	useSupportAgentSubmitSRMutation,
	useSupportAgentSubmitSRWithCardMutation,
	useLazyGetPageOfAllServiceRequestInstancesQuery,
	useLazyGetServiceRequestInstanceDetailsByIdQuery,
	useGetAllRequestTypesQuery,
} = customerSupportEndpoint;
